import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { adminAnalytics } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, AreaChart, Area, CartesianGrid } from "recharts";
import { Trophy } from "lucide-react";

const COLORS = ["#2D5A27", "#D97706", "#15803D", "#0E7490", "#B45309", "#65A30D", "#BE123C", "#334155"];
const inr = (v) => `₹${Math.round(v).toLocaleString("en-IN")}`;

const Panel = ({ title, children, testId }) => (
  <div className="bg-white border border-[#E2E8F0] rounded-md p-5" data-testid={testId}>
    <h3 className="font-heading font-semibold text-[#1E293B] mb-4">{title}</h3>
    {children}
  </div>
);

const Empty = () => <p className="text-sm text-slate-500 py-8 text-center">No sales data yet</p>;

const TopProducts = ({ items, lang }) => (
  <ol className="space-y-2" data-testid="top-products-list">
    {items.map((p, i) => (
      <li key={p.product_id} className="flex items-center gap-3" data-testid={`top-product-${i}`}>
        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${i < 3 ? "bg-[#D97706] text-white" : "bg-[#EFEBE2] text-slate-700"}`}>{i + 1}</span>
        <span className={`flex-1 text-sm truncate ${lang === "hi" ? "font-devanagari" : ""}`}>{lang === "hi" ? p.name_hi : p.name_en}</span>
        <span className="text-xs text-slate-500 shrink-0">{p.quantity} sold</span>
        <span className="text-sm font-semibold text-[#2D5A27] shrink-0 w-20 text-right">{inr(p.revenue)}</span>
      </li>
    ))}
  </ol>
);

const SalesAnalytics = () => {
  const { t, lang, adminToken } = useApp();
  const [data, setData] = useState(null);

  useEffect(() => { adminAnalytics(adminToken).then(setData).catch(() => setData(null)); }, [adminToken]);
  if (!data) return null;

  const cats = data.category_revenue.map((c) => ({ ...c, label: c.category === "other" ? "Other" : t(c.category) }));

  return (
    <div className="mt-8" data-testid="sales-analytics">
      <h2 className="font-heading font-semibold text-lg text-[#1E293B] mb-3">
        {lang === "hi" ? "बिक्री विश्लेषण" : "Sales Analytics"}
        <span className="ml-2 text-xs font-normal text-slate-500">({data.order_count} orders, excl. cancelled)</span>
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Panel title={lang === "hi" ? "श्रेणी अनुसार कमाई" : "Category-wise Revenue"} testId="category-revenue-chart">
          {cats.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={Math.max(180, cats.length * 38)}>
              <BarChart data={cats} layout="vertical" margin={{ left: 10, right: 20 }}>
                <XAxis type="number" tickFormatter={inr} fontSize={11} />
                <YAxis type="category" dataKey="label" width={130} fontSize={11} />
                <Tooltip formatter={(v) => inr(v)} />
                <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                  {cats.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel title={<span className="flex items-center gap-2"><Trophy className="w-4 h-4 text-[#D97706]" />{lang === "hi" ? "सबसे ज़्यादा बिकने वाले" : "Best-Selling Products"}</span>} testId="best-sellers">
          {data.top_products.length === 0 ? <Empty /> : <TopProducts items={data.top_products} lang={lang} />}
        </Panel>
        <Panel title={lang === "hi" ? "दैनिक कमाई (पिछले 30 दिन)" : "Daily Revenue (last 30 days)"} testId="daily-revenue-chart">
          {data.daily_revenue.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={data.daily_revenue}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" fontSize={10} tickFormatter={(d) => d.slice(5)} />
                <YAxis fontSize={10} tickFormatter={inr} width={60} />
                <Tooltip formatter={(v) => inr(v)} />
                <Area type="monotone" dataKey="revenue" stroke="#2D5A27" fill="#2D5A27" fillOpacity={0.15} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Panel>
        <Panel title={lang === "hi" ? "भुगतान तरीका" : "Payment Split"} testId="payment-split">
          {data.payment_split.length === 0 ? <Empty /> : (
            <div className="space-y-3">
              {data.payment_split.map((p) => {
                const total = data.payment_split.reduce((s, x) => s + x.revenue, 0) || 1;
                const pct = Math.round((p.revenue / total) * 100);
                return (
                  <div key={p.method} data-testid={`payment-split-${p.method}`}>
                    <div className="flex justify-between text-sm mb-1"><span className="font-semibold">{p.method}</span><span>{inr(p.revenue)} · {pct}%</span></div>
                    <div className="h-2.5 rounded-full bg-[#EFEBE2] overflow-hidden"><div className="h-full bg-[#2D5A27]" style={{ width: `${pct}%` }} /></div>
                  </div>
                );
              })}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
};

export default SalesAnalytics;
